import {
  LEGACY_EMPLOYEE_FILTER_KEYS,
  migrateLegacyEmployeeFilters,
} from "@/pages/employees/index/employee-filter-migration.ts";
import { COLUMN_FILTERS_PARAM } from "@/pages/employees/index/use-employee-column-filters.ts";
import {
  buildCustomTableKey,
  ApiEmployeeColumnFilter,
  ApiEmployeeColumnFilters,
} from "@/types/employees/employee-column.types.ts";
import {
  ApiPeopleAnalyticsAggregation,
  ApiPeopleAnalyticsColumn,
  ApiPeopleAnalyticsStoredColumnFilter,
  ApiPeopleAnalyticsValueType,
  ApiPeopleAnalyticsWidget,
  ApiPeopleAnalyticsWidgetFilter,
  ApiPeopleAnalyticsWidgetType,
} from "@/types/reporting/people-analytics.types.ts";
import { ApiPaginationMetadata } from "@skailer-com/uikit/core";
import { parse } from "date-fns";

export enum PeopleAnalyticsView {
  table = "table",
  chart = "chart",
}

export enum PeopleAnalyticsChartType {
  bar = "bar",
  line = "line",
}

// Синтетическая серия «число сотрудников в группе» — не колонка каталога, считается из employee_ids
export const TOTAL_EMPLOYEES_SERIES_ID = "__total_employees";
export const PEOPLE_ANALYTICS_METRICS_PARAM = "metrics";
export const PEOPLE_ANALYTICS_WIDGET_PARAM = "widget";
export const PEOPLE_ANALYTICS_DATE_GRAIN_PARAM = "date_grain";
export const LIFECYCLE_FILTER_PARAM = "employee_lifecycle_statuses";
export const IS_MANAGER_FILTER_PARAM = "is_manager";
export const PEOPLE_ANALYTICS_GROUP_BY_PARAM = "group_by";
export const PEOPLE_ANALYTICS_CURRENCY_PARAM = "currency";
export const EMPLOYEE_GROUP_COLUMN_ID = "employee.id";

const EMPLOYEE_REPORT_COLUMN_IDS = [
  "employee.position.job_profile",
  "employee.position.department",
  "employee.position.manager",
];

const WIDGET_FILTER_ARRAY_KEYS: Exclude<
  keyof ApiPeopleAnalyticsWidgetFilter,
  "text" | keyof ApiEmployeeColumnFilters
>[] = [
  "employee_ids",
  "department_ids",
  "division_ids",
  "location_ids",
  "job_profile_ids",
  "job_role_ids",
  "team_ids",
  "employment_type_ids",
  "employment_contract_ids",
  "employee_lifecycle_statuses",
];

export function isWidgetChart(widget: ApiPeopleAnalyticsWidget): boolean {
  return (
    widget.visualization.widget_type === ApiPeopleAnalyticsWidgetType.bar_chart ||
    widget.visualization.widget_type === ApiPeopleAnalyticsWidgetType.line_chart
  );
}

// Разворачивает сохранённый виджет в параметры билдера: экран целиком строится из URL
export function widgetToSearchParams(widget: ApiPeopleAnalyticsWidget): URLSearchParams {
  const params = new URLSearchParams();
  params.set(PEOPLE_ANALYTICS_WIDGET_PARAM, String(widget.id));
  if (widget.group_by_column_id) params.set("group_by", widget.group_by_column_id);
  if (isWidgetChart(widget)) {
    params.set(
      "chart",
      widget.visualization.widget_type === ApiPeopleAnalyticsWidgetType.line_chart
        ? PeopleAnalyticsChartType.line
        : PeopleAnalyticsChartType.bar
    );
  }
  params.set(PEOPLE_ANALYTICS_METRICS_PARAM, encodeMetricsParam(widget.metrics));
  if (widget.date_grain) params.set(PEOPLE_ANALYTICS_DATE_GRAIN_PARAM, widget.date_grain);
  if (widget.compensation?.currency) {
    params.set(PEOPLE_ANALYTICS_CURRENCY_PARAM, widget.compensation.currency);
  }
  if (widget.filter.text) params.set("search", widget.filter.text);
  if (typeof widget.filter.is_manager === "boolean") {
    params.set(IS_MANAGER_FILTER_PARAM, String(widget.filter.is_manager));
  }
  WIDGET_FILTER_ARRAY_KEYS.forEach((key) => {
    const value = widget.filter[key];
    if (Array.isArray(value)) setFilterParam(params, key, value);
  });
  const columnFilters = storedFiltersToColumnFilters(widget.filter);
  if (columnFilters.length > 0) params.set(COLUMN_FILTERS_PARAM, JSON.stringify(columnFilters));
  return params;
}

const FILTER_VALUE_KEYS = [
  "text",
  "text_array",
  "number_from",
  "number_to",
  "date_filter",
  "datetime_from",
  "datetime_to",
  "option_ids",
  "switch",
  "employee_ids",
  "is_empty",
] as const;

const COLUMN_FILTER_LIST_KEYS: (keyof ApiEmployeeColumnFilters)[] = [
  "personal_filter",
  "position_filter",
  "employment_filter",
  "compensation_filter",
  "termination_filter",
  "additional_compensation_filter",
  "custom_fields_filter",
  "custom_table_filter",
];

// Без `field` виджет пришёл с бэка, который ключ не отдаёт: собрать его можно только у пользовательской таблицы
function buildStoredFilterKey(
  listKey: keyof ApiEmployeeColumnFilters,
  item: ApiPeopleAnalyticsStoredColumnFilter
): string | undefined {
  if (item.field) return item.field;
  if (listKey !== "custom_table_filter" || item.table_id == null || !item.slug) return undefined;
  if (item.table_type && item.table_type !== "custom") return undefined;
  return buildCustomTableKey(item.table_id, item.slug);
}

export function storedFiltersToColumnFilters(
  filter: ApiPeopleAnalyticsWidgetFilter
): ApiEmployeeColumnFilter[] {
  return COLUMN_FILTER_LIST_KEYS.flatMap((listKey) =>
    (filter[listKey] ?? []).flatMap((item) => {
      const field = buildStoredFilterKey(listKey, item);
      if (!field) return [];
      const value = Object.fromEntries(
        FILTER_VALUE_KEYS.filter((key) => item[key] != null).map((key) => [key, item[key]])
      );
      return [{ ...value, field }];
    })
  );
}

const LEGACY_FILTER_PARAMS = [...LEGACY_EMPLOYEE_FILTER_KEYS, IS_MANAGER_FILTER_PARAM];

export function hasLegacyFilterParams(params: URLSearchParams): boolean {
  return LEGACY_FILTER_PARAMS.some((key) => params.has(key));
}

export function migrateLegacyFilterParams(
  source: URLSearchParams,
  existingFilters: ApiEmployeeColumnFilter[],
  availableFields: ReadonlySet<string>
): URLSearchParams {
  const legacy: Record<string, unknown> = Object.fromEntries(
    LEGACY_EMPLOYEE_FILTER_KEYS.flatMap((key) => {
      const value = source.get(key);
      return value === null ? [] : [[key, value]];
    })
  );
  const isManager = source.get(IS_MANAGER_FILTER_PARAM);
  if (isManager === "true" || isManager === "false") legacy.is_manager = isManager === "true";

  const params = new URLSearchParams(source);
  LEGACY_FILTER_PARAMS.forEach((key) => params.delete(key));
  const migrated = migrateLegacyEmployeeFilters(legacy, availableFields, existingFilters);
  if (migrated.length) params.set(COLUMN_FILTERS_PARAM, JSON.stringify(migrated));
  else params.delete(COLUMN_FILTERS_PARAM);
  return params;
}

export function setFilterParam(
  params: URLSearchParams,
  name: string,
  values: readonly (string | number)[] | undefined
): void {
  const joined = values && values.length > 0 ? values.join(",") : "";
  if (joined) params.set(name, joined);
  else if (name === LIFECYCLE_FILTER_PARAM) params.set(name, "");
  else params.delete(name);
}

export interface PeopleAnalyticsMetricEntry {
  column_id: string;
  aggregation?: ApiPeopleAnalyticsAggregation | null;
}

export function encodeMetricsParam(entries: PeopleAnalyticsMetricEntry[]): string {
  return entries
    .map((entry) =>
      entry.aggregation ? `${entry.column_id}|${entry.aggregation}` : entry.column_id
    )
    .join(",");
}

export function parseMetricsParam(
  raw: string | null
): { columnIds: string[]; aggregations: Record<string, ApiPeopleAnalyticsAggregation> } | null {
  if (raw === null) return null;
  const columnIds: string[] = [];
  const aggregations: Record<string, ApiPeopleAnalyticsAggregation> = {};
  raw
    .split(",")
    .filter(Boolean)
    .forEach((entry) => {
      const [id, aggregation] = entry.split("|");
      columnIds.push(id);
      if (aggregation) aggregations[id] = aggregation as ApiPeopleAnalyticsAggregation;
    });
  return { columnIds, aggregations };
}

export function isChartableColumn(
  column: ApiPeopleAnalyticsColumn,
  aggregation: ApiPeopleAnalyticsAggregation
): boolean {
  if (aggregation === ApiPeopleAnalyticsAggregation.values) return false;
  if (
    aggregation === ApiPeopleAnalyticsAggregation.count ||
    aggregation === ApiPeopleAnalyticsAggregation.count_distinct
  ) {
    return true;
  }
  return (
    column.value_type === ApiPeopleAnalyticsValueType.number ||
    column.value_type === ApiPeopleAnalyticsValueType.money
  );
}

export function isMoneyAggregation(
  column: ApiPeopleAnalyticsColumn,
  aggregation: ApiPeopleAnalyticsAggregation
): boolean {
  return (
    column.value_type === ApiPeopleAnalyticsValueType.money &&
    aggregation !== ApiPeopleAnalyticsAggregation.count &&
    aggregation !== ApiPeopleAnalyticsAggregation.count_distinct
  );
}

export const PEOPLE_ANALYTICS_PAGE_SIZE = 50;

export function paginateRows<T>(
  rows: T[],
  page: number,
  pageSize: number
): { items: T[]; metadata: ApiPaginationMetadata } {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const pageNumber = Math.min(Math.max(1, page), totalPages);
  const items = rows.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
  return {
    items,
    metadata: {
      page_number: pageNumber,
      items_per_page: pageSize,
      items: items.length,
      total_pages: totalPages,
      total_items: rows.length,
    },
  };
}

export function resolveDefaultAggregation(
  column: ApiPeopleAnalyticsColumn | undefined,
  isEmployeeGrouping: boolean
): ApiPeopleAnalyticsAggregation {
  if (isEmployeeGrouping && column?.aggregations.includes(ApiPeopleAnalyticsAggregation.values)) {
    return ApiPeopleAnalyticsAggregation.values;
  }
  return ApiPeopleAnalyticsAggregation.count;
}

export function buildEmployeeReportSearchParams(
  catalogColumns: ApiPeopleAnalyticsColumn[]
): URLSearchParams | null {
  const isEmployeeGroupable = catalogColumns.some(
    (column) => column.id === EMPLOYEE_GROUP_COLUMN_ID && column.is_groupable
  );
  if (!isEmployeeGroupable) return null;

  const metrics = EMPLOYEE_REPORT_COLUMN_IDS.flatMap((columnId) => {
    const column = catalogColumns.find((item) => item.id === columnId);
    if (!column) return [];
    return [{ column_id: columnId, aggregation: resolveDefaultAggregation(column, true) }];
  });

  const params = new URLSearchParams();
  params.set(PEOPLE_ANALYTICS_GROUP_BY_PARAM, EMPLOYEE_GROUP_COLUMN_ID);
  if (metrics.length > 0) params.set(PEOPLE_ANALYTICS_METRICS_PARAM, encodeMetricsParam(metrics));
  return params;
}

interface ValuesCellLabels {
  renderDate: (date: Date) => string;
  renderMoney: (value: string) => string;
  yes: string;
  no: string;
}

export function formatValuesCell(
  text: string,
  valueType: ApiPeopleAnalyticsValueType,
  labels: ValuesCellLabels
): string {
  if (valueType === ApiPeopleAnalyticsValueType.date) {
    return text
      .split(", ")
      .map((part) => {
        const date = parse(part, "yyyy-MM-dd", new Date());
        return isNaN(date.getTime()) ? part : labels.renderDate(date);
      })
      .join(", ");
  }
  if (valueType === ApiPeopleAnalyticsValueType.bool) {
    return text
      .split(", ")
      .map((part) => {
        const normalized = part.toLowerCase();
        if (normalized === "true") return labels.yes;
        if (normalized === "false") return labels.no;
        return part;
      })
      .join(", ");
  }
  if (valueType === ApiPeopleAnalyticsValueType.money) {
    return text
      .split(", ")
      .map((part) => labels.renderMoney(part))
      .join(", ");
  }
  return text;
}
